"use client";

import Link from "next/link";
import Image from "next/image";
import { useTheme } from "@/components/public/theme-provider";
import { useEffect, useState } from "react";
import { useRouter, usePathname } from "next/navigation";
import dynamic from "next/dynamic";
import { MegaMenu } from "@/components/public/MegaMenu";
import { megaMenuData, isPublicPage } from "@/lib/navigation";
import { ModernButton } from "@/components/ui/ModernButton";
import { getAuthUrl } from "@/lib/appStage";

const MobileMenu = dynamic(
  () => import("@/components/public/MobileMenu").then((mod) => mod.MobileMenu),
  { ssr: false }
);

export function LandingTopBar() {
  useTheme(); // Keep for theme context
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileMenuView, setMobileMenuView] = useState<'main' | string>('main');
  const router = useRouter();
  const pathname = usePathname();

  // Disable body scroll when mobile menu is open and reset view on close
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
      // Reset to main view when menu closes for fresh experience on reopen
      const timeout = setTimeout(() => setMobileMenuView('main'), 300);
      return () => clearTimeout(timeout);
    }
    
    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [mobileMenuOpen]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogoClick = () => {
    if (pathname === '/') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      router.push('/');
    }
  };

  const registerUrl = getAuthUrl('register');
  const loginUrl = getAuthUrl('login');

  // Check if we're on a public page
  const publicPage = isPublicPage(pathname || '');
  const isHomepage = pathname === '/';
  // Super modern professional top bar with dark theme
  return (
    <header 
      className={`absolute top-0 left-0 right-0 z-50 sm:px-0 md:px-6 xl:px-6 ${
        mobileMenuOpen || !isHomepage ? 'bg-white' : ''
      }`}
    >
      <div className="w-full">
        {/* Consistent responsive padding: px-4 mobile, px-6 tablet, grid-aligned desktop */}
        <div className="px-4 sm:px-6 xl:px-0 xl:mx-[16.67%]">
          <div className="flex items-center h-16">{/* Back to h-16 */}
          {/* Logo with subtle animation */}
          <div 
            onClick={handleLogoClick} 
            className="cursor-pointer flex-shrink-0"
          >
            <div className="relative">
              {/* Desktop Logo - consistent size */}
              <Image
                src={publicPage ? "/assets/REPLENICO/logos/svgs/Formerly Logo_Dark.svg" : "/assets/REPLENICO/logos/svgs/Formerly Logo_White.svg"}
                alt="Replenico Logo"
                width={180}
                height={54}
                style={{ width: 'auto', height: '3rem' }}
                className="hidden min-[1750px]:block object-contain hover:opacity-80 transition-opacity duration-200"
                priority
              />
              
              {/* Tablet/Mobile Logo - consistent size */}
              {!mobileMenuOpen && (
                <Image
                  src={publicPage ? "/assets/REPLENICO/logos/svgs/replenico-logo-text-dark.svg" : "/assets/REPLENICO/logos/svgs/replenico-logo-text-white.svg"}
                  alt="Replenico Logo"
                  width={120}
                  height={36}
                  style={{ width: 'auto', height: '2rem' }}
                  className="min-[1750px]:hidden object-contain"
                  priority
                />
              )}
              
              {/* Mobile Menu Header Elements - Only show when menu is open */}
              {mobileMenuOpen && (
                <div className="min-[1750px]:hidden">
                  {mobileMenuView === 'main' ? (
                    <Image
                      src="/assets/REPLENICO/logos/svgs/replenico-logo-text-dark.svg"
                      alt="Replenico Logo"
                      width={120}
                      height={36}
                      style={{ width: 'auto', height: '2rem' }}
                      className="object-contain"
                    />
                  ) : (
                    <button
                      onClick={() => setMobileMenuView('main')}
                      className="p-2 rounded-lg hover:bg-gray-100 transition-all duration-300 group cursor-pointer"
                    >
                      <svg className="w-6 h-6 text-gray-700 transition-transform duration-300 group-hover:-translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                  )}
                </div>
              )}
              <div className="absolute inset-0 bg-[color:var(--homepage-accent)]/10 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur-sm -z-10"></div>
            </div>
          </div>

          {/* Navigation with modern styling */}
          <nav className="hidden min-[1750px]:flex items-center gap-2 ml-8 flex-1 min-w-0">
            {megaMenuData.map((menu: any, index: number) => (
              <MegaMenu
                key={index}
                title={menu.title}
                sections={menu.sections}
                isPublicPage={publicPage}
              />
            ))}
            <Link
              href="/contact"
              className={`relative px-4 py-2 rounded-lg text-base font-medium transition-all duration-300 group ${
                publicPage 
                  ? 'text-[color:var(--homepage-primary)] hover:text-[var(--homepage-accent-purple)]' 
                  : 'text-[color:var(--homepage-navbar-text)] hover:text-[color:var(--homepage-navbar-hover)]'
              }`}
            >
              <span className="relative z-10">Contact</span>
            </Link>
            <Link
              href="/book-a-demo"
              className={`relative ml-2 xl:ml-3 px-5 py-2 rounded-full text-base font-semibold transition-all duration-300 group border shadow-sm ${
                publicPage
                  ? 'text-[#8c52ff] border-purple-200 bg-purple-50 hover:bg-purple-100 hover:border-purple-300'
                  : 'text-[color:var(--homepage-navbar-text)] border-white/20 bg-white/10 hover:bg-white/20 hover:text-[color:var(--homepage-navbar-hover)]'
              }`}
            >
              <span className="relative z-10 inline-flex items-center gap-1.5">
                Book a Demo
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-current opacity-70" />
              </span>
            </Link>
            {/* HIDDEN ROUTE - Uncomment to show in navigation */}
            {/* <Link
              href="/pricing"
              className={`relative px-4 py-2 rounded-lg text-base font-medium transition-all duration-300 group ${
                publicPage 
                  ? 'text-[color:var(--homepage-primary)] hover:text-[var(--homepage-accent-purple)]' 
                  : 'text-[color:var(--homepage-navbar-text)] hover:text-[color:var(--homepage-navbar-hover)]'
              }`}
            >
              <span className="relative z-10">Pricing</span>
            </Link> */}
          </nav>


          {/* Auth Buttons with modern styling - Hidden on mobile */}
          <div className="hidden min-[1750px]:flex items-center space-x-3 ml-auto">
            <a
              href={registerUrl}
              className={`relative px-4 py-2 rounded-lg text-base font-medium transition-all duration-300 group ${
                publicPage 
                  ? 'text-[color:var(--homepage-primary)] hover:text-[#8c52ff]' 
                  : 'text-[color:var(--homepage-navbar-text)] hover:text-[color:var(--homepage-navbar-hover)]'
              }`}
            >
              <span className="relative z-10">Register</span>
            </a>
            <ModernButton
              href={loginUrl}
              variant="primary"
              size="md"
              showArrow={true}
            >
              Login
            </ModernButton>
          </div>

          {/* Compact actions + menu for containered screens */}
          <div className="min-[1750px]:hidden ml-auto flex items-center gap-2">
            <div className="hidden sm:flex items-center gap-2">
              <a
                href={registerUrl}
                className="px-3.5 py-2 rounded-full text-sm font-semibold border border-white/70 bg-white/95 text-gray-900 hover:bg-white hover:border-white shadow-sm transition-all duration-200"
              >
                Register
              </a>
              <a
                href={loginUrl}
                className="px-3.5 py-2 rounded-full text-sm font-semibold bg-gray-900 text-white hover:bg-gray-800 transition-all duration-200"
              >
                Login
              </a>
            </div>

            <Link
              href="/book-a-demo"
              className={`hidden xl:inline-flex px-4 py-2 rounded-full text-sm font-semibold items-center gap-1.5 border transition-all duration-200 ${
                publicPage
                  ? 'text-[#8c52ff] border-purple-200 bg-purple-50 hover:bg-purple-100 hover:border-purple-300'
                  : 'text-[color:var(--homepage-navbar-text)] border-white/20 bg-white/10 hover:bg-white/20 hover:text-[color:var(--homepage-navbar-hover)]'
              }`}
            >
              Book a Demo
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-current opacity-70" />
            </Link>

            {!mobileMenuOpen && (
              <button
                onClick={() => setMobileMenuOpen(true)}
                className="p-1.5 rounded-xl transition-all duration-300 hover:bg-black/5"
              >
                <Image
                  src={publicPage ? "/assets/REPLENICO/logos/svgs/replenico-logo-3-lines-dark.svg" : "/assets/REPLENICO/logos/svgs/replenico-logo-3-lines-white.svg"}
                  alt="Menu"
                  width={48}
                  height={48}
                  className="h-10 w-auto object-fill transition-all duration-300"
                />
              </button>
            )}
          </div>

        </div>
      </div>
    </div>
      {/* Mobile Menu Dropdown */}
      <div 
        className={`min-[1750px]:hidden fixed inset-0 z-40 transition-all duration-300 ease-out ${
          mobileMenuOpen 
            ? 'opacity-100 pointer-events-auto' 
            : 'opacity-0 pointer-events-none'
        }`}
      >
        {/* Backdrop */}
        <div 
          className={`absolute inset-0 bg-white transition-transform duration-300 ease-out ${
            mobileMenuOpen ? 'translate-y-0' : '-translate-y-full'
          }`}
        />
        
        {/* Content */}
        <div className="relative h-full flex flex-col">
          {/* Fixed Header Area */}
          <div className="h-16 border-b border-gray-100 flex items-center justify-between px-4 sm:px-6 flex-shrink-0 bg-white">
            {/* Left side - Logo or Back button */}
            <div className="flex items-center min-w-[48px]">
              {mobileMenuView === 'main' ? (
                <Image
                  src="/assets/REPLENICO/logos/svgs/replenico-logo-text-dark.svg"
                  alt="Replenico Logo"
                  width={120}
                  height={36}
                  style={{ width: 'auto', height: '2rem' }}
                  className="object-contain"
                />
              ) : (
                <button
                  onClick={() => setMobileMenuView('main')}
                  className="p-2 -ml-2 rounded-xl hover:bg-gray-100 active:bg-gray-200 transition-all duration-200 group cursor-pointer"
                >
                  <svg className="w-6 h-6 text-gray-600 transition-transform duration-200 group-hover:-translate-x-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
              )}
            </div>

            {/* Center - Title when in nested view */}
            <div className="flex-1 flex justify-center">
              {mobileMenuView !== 'main' && (
                <h2 className="text-gray-900 font-bold text-lg tracking-tight">
                  {megaMenuData.find((menu: any) => menu.title === mobileMenuView)?.title}
                </h2>
              )}
            </div>

            {/* Right side - Close button */}
            <div className="min-w-[48px] flex justify-end">
              <button
                onClick={() => setMobileMenuOpen(false)}
                className="p-2 -mr-2 rounded-xl hover:bg-gray-100 active:bg-gray-200 transition-all duration-200 group"
              >
                <svg className="w-6 h-6 text-gray-600 group-hover:text-gray-800 transition-colors duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Scrollable Content Area */}
          <div className="flex-1 overflow-hidden">
            <MobileMenu 
              menuData={megaMenuData} 
              onItemClick={() => setMobileMenuOpen(false)}
              currentView={mobileMenuView}
              setCurrentView={setMobileMenuView}
            />
          </div>
        </div>
      </div>
    </header>
  );
}